import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { showDeleteConfirm } from '../utils/sweetAlert';
import {
  fetchIndustries,
  createIndustry,
  updateIndustry,
  deleteIndustry,
} from '../utils/adminApiService';
import logger from '../utils/logger';
import { useDebounce } from '../hooks/useDebounce';
import AdminIndustriesHeader from './AdminIndustries/AdminIndustriesHeader';
import AdminIndustriesStats from './AdminIndustries/AdminIndustriesStats';
import AdminIndustriesToolbar from './AdminIndustries/AdminIndustriesToolbar';
import AdminIndustriesTable from './AdminIndustries/AdminIndustriesTable';
import AdminIndustriesPagination from './AdminIndustries/AdminIndustriesPagination';
import AdminIndustriesAddModal from './AdminIndustries/AdminIndustriesAddModal';
import AdminIndustriesEditModal from './AdminIndustries/AdminIndustriesEditModal';

const AdminIndustries = () => {
  const { user, hasRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [filterRegion, setFilterRegion] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    region: 'naics'
  });

  // Wait for auth to load before checking access
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-4" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6" />
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Access control
  if (!user || !hasRole('admin')) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400">You need admin privileges to access this page.</p>
          <button 
            onClick={() => navigate('/admin')} 
            className="mt-4 px-4 py-2 bg-emerald-600 dark:bg-emerald-700 text-white rounded-md hover:bg-emerald-700 dark:hover:bg-emerald-600"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  const [initialLoad, setInitialLoad] = useState(true);

  // Initial load - only on mount or when auth changes
  useEffect(() => {
    if (authLoading) return;
    if (!user || !hasRole('admin')) return;
    loadIndustries(true);
    setInitialLoad(false);
  }, [authLoading, user]);

  // Reload when filter changes (but don't show loading spinner after initial load)
  useEffect(() => {
    if (authLoading || initialLoad) return; // Skip if still on initial load
    if (!user || !hasRole('admin')) return;
    loadIndustries(false); // Don't show loading for filter changes
  }, [filterRegion]);

  const loadIndustries = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      logger.log('🔍 Loading industries with auth:', { user: user?.email, role: user?.role });
      
      const data = await fetchIndustries(true, filterRegion !== 'all' ? filterRegion : null);
      
      logger.log('✅ Industries loaded:', data);
      
      if (data.success) {
        setIndustries(data.industries || []);
      }
    } catch (error) {
      logger.error('❌ Error loading industries:', error);
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      } else {
        logger.error('Error loading industries:', error);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      region: 'naics'
    });
  };

  const handleAddIndustry = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.region) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const result = await createIndustry(formData);
      
      if (result.success) {
        setShowAddModal(false);
        resetForm();
        await loadIndustries();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (industry) => {
    setEditingIndustry(industry);
    setFormData({
      name: industry.name || '',
      description: industry.description || '',
      region: industry.region || 'naics'
    });
    setShowEditModal(true);
  };

  const handleUpdateIndustry = async (e) => {
    e.preventDefault();
    if (!editingIndustry) return;

    try {
      setSubmitting(true);
      const result = await updateIndustry(editingIndustry.id, formData);
      
      if (result.success) {
        setShowEditModal(false);
        setEditingIndustry(null);
        resetForm();
        await loadIndustries();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteIndustry = async (industryId) => {
    const confirmed = await showDeleteConfirm('this industry');
    if (!confirmed) {
      return;
    }

    try {
      const result = await deleteIndustry(industryId);
      
      if (result.success) {
        await loadIndustries();
      }
    } catch (error) {
      if (error.message === 'UNAUTHORIZED') {
        navigate('/login');
      }
    }
  };

  const handleViewSubIndustries = (id, name) => {
    navigate('/admin/subindustry', { state: { industryId: id, industryName: name } });
  };

  // Client-side filtering with debounced search
  const filteredIndustries = industries.filter(ind => {
    const matchesSearch = ind.name.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) || 
                         (ind.description && ind.description.toLowerCase().includes(debouncedSearchQuery.toLowerCase()));
    const matchesRegion = filterRegion === 'all' || ind.region === filterRegion;
    return matchesSearch && matchesRegion;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredIndustries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedIndustries = filteredIndustries.slice(startIndex, endIndex);

  // Reset to page 1 when filters change (use debounced search)
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, filterRegion]);

  // Calculate totals
  const totalQuestions = industries.reduce((sum, ind) => sum + (ind.question_count || 0), 0);
  const totalSubIndustries = industries.reduce(
    (sum, ind) => sum + (ind.sub_industries?.length || 0),
    0,
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      {/* Toaster removed - using global Toaster from App.js */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminIndustriesHeader
          loading={loading}
          total={industries.length}
          onBack={() => navigate('/admin')}
        />

        <AdminIndustriesStats
          loading={loading}
          totalIndustries={industries.length}
          totalQuestions={totalQuestions}
          totalSubIndustries={totalSubIndustries}
        />

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <AdminIndustriesToolbar
            searchQuery={searchQuery}
            onSearchChange={(value) => setSearchQuery(value)}
            filterRegion={filterRegion}
            onFilterRegionChange={(value) => setFilterRegion(value)}
            onAddClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
          />

          <AdminIndustriesTable
            loading={loading}
            industries={paginatedIndustries}
            onViewSubIndustries={handleViewSubIndustries}
            onEdit={handleEditClick}
            onDelete={handleDeleteIndustry}
          />

          <AdminIndustriesPagination
            filteredCount={filteredIndustries.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={setCurrentPage}
          />
        </div>

        <AdminIndustriesAddModal
          visible={showAddModal}
          formData={formData}
          submitting={submitting}
          onClose={() => {
            setShowAddModal(false);
            resetForm();
          }}
          onChange={handleFormChange}
          onSubmit={handleAddIndustry}
        />

        <AdminIndustriesEditModal
          visible={showEditModal}
          editingIndustry={editingIndustry}
          formData={formData}
          submitting={submitting}
          onClose={() => {
            setShowEditModal(false);
            setEditingIndustry(null);
            resetForm();
          }}
          onChange={handleFormChange}
          onSubmit={handleUpdateIndustry}
        />
      </div>
    </div>
  );
};

export default AdminIndustries;
